import { Link, Outlet, useNavigate, useLocation } from "react-router-dom"
import { Button } from "./ui/button"
import { 
  LayoutDashboard, 
  Users, 
  Gift, 
  Building2, 
  GitFork, 
  Ticket,
  LogOut,
  Shield,
  BookOpen,
  Moon,
  Sun
} from "lucide-react"
import { useEffect, useState } from "react"

const AdminLayout = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const auth = window.localStorage.getItem('auth')
  const user = auth ? JSON.parse(auth).user : null
  const [isDark, setIsDark] = useState(false)

  useEffect(() => {
    // Check for saved theme preference or default to light mode
    const savedTheme = localStorage.getItem('theme')
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
    
    if (savedTheme === 'dark' || (!savedTheme && prefersDark)) {
      setIsDark(true)
      document.documentElement.classList.add('dark')
    }
  }, [])

  const toggleTheme = () => {
    if (isDark) {
      document.documentElement.classList.remove('dark')
      localStorage.setItem('theme', 'light')
      setIsDark(false)
    } else {
      document.documentElement.classList.add('dark')
      localStorage.setItem('theme', 'dark')
      setIsDark(true)
    }
  }

  const handleLogout = () => {
    window.localStorage.removeItem('auth')
    window.location.href = '/'
  }

  const navItems = [
    { path: '/admin', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/admin/users', icon: Users, label: 'Users' },
    { path: '/admin/repos', icon: GitFork, label: 'Repositories' },
    { path: '/admin/store', icon: Gift, label: 'Store' },
    { path: '/admin/sponsors', icon: Building2, label: 'Sponsors' },
    { path: '/admin/redemptions', icon: Ticket, label: 'Redemptions' },
    { path: '/admin/resources', icon: BookOpen, label: 'Resources' },
  ]

  const isActive = (path) => {
    if (path === '/admin') {
      return location.pathname === '/admin'
    }
    return location.pathname.startsWith(path)
  }

  // Redirect if not admin
  if (!user?.isAdmin) {
    navigate('/dashboard')
    return null
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Top Header */}
      <header className="border-b bg-background sticky top-0 z-50">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <Shield className="h-6 w-6 text-primary shrink-0" />
              <h1 className="text-lg sm:text-xl font-bold truncate">FOSSIT Admin</h1>
            </div>
            <div className="flex items-center gap-2 sm:gap-4 shrink-0">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={toggleTheme}
                className="gap-2"
                aria-label="Toggle theme"
              >
                {isDark ? (
                  <Sun className="h-5 w-5" />
                ) : (
                  <Moon className="h-5 w-5" />
                )}
              </Button>
              <Link to="/dashboard" className="hidden sm:block">
                <Button variant="outline" size="sm">
                  Back to Dashboard
                </Button>
              </Link>
              {user && (
                <div className="hidden md:flex items-center gap-2">
                  <img 
                    src={user.avatarUrl} 
                    alt={user.username}
                    className="h-8 w-8 rounded-full"
                  />
                  <span className="text-sm font-medium">{user.username}</span>
                </div>
              )}
              <Button variant="outline" size="sm" onClick={handleLogout}>
                <LogOut className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6">
        <div className="grid grid-cols-12 gap-6">
          {/* Sidebar Navigation */}
          <aside className="col-span-12 md:col-span-3 lg:col-span-2">
            <nav className="space-y-2 sticky top-20">
              {navItems.map((item) => {
                const Icon = item.icon
                const active = isActive(item.path)
                return (
                  <Link key={item.path} to={item.path}>
                    <Button
                      variant={active ? "default" : "ghost"}
                      className="w-full justify-start gap-2"
                      size="sm"
                    >
                      <Icon className="h-4 w-4" />
                      {item.label}
                    </Button>
                  </Link>
                )
              })}
            </nav>
          </aside>

          {/* Main Content */}
          <main className="col-span-12 md:col-span-9 lg:col-span-10">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  )
}

export default AdminLayout
